import { useCustomerOrderApiV1 } from '../../clients/customer-api/order/customer-order-api-v1';
import { CustomerOrder } from '../../clients/customer-api/order/customer-order';
import { Location } from '../../clients/quipli-service/location/location';
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { Pagination, PaginationRequest } from '../../clients/quipli-service/pagination/pagination';
import _, { sum } from 'lodash';
import { OrderReturnStatus, OrderStatus } from '../../clients/quipli-service/order/order';
import { Paginator } from '../../components/paginator/paginator-v2';
import './customer-order-list.scss';
import classNames from 'classnames';
import { Interval } from 'luxon';
import { useCustomerLocationApiV1 } from '../../clients/customer-api/location/customer-location-api-v1';
import { convertToMoney, displayMoneyFormat } from '../../util/util';
import { TenantContext } from '../../context/tenant-context';
import { Card } from '../../components/card/card';
import { useCustomer } from '../../hooks/use-customer-context';
import { InvoiceSearchSummaryData } from '../../clients/quipli-service/invoice/invoice';
import { Spinner } from '../../components/spinner/spinner';
import { Currency } from 'dinero.js';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { EcommerceButton } from '../../components/form/button';
import { CustomerPaymentModal } from '../payment/components/customer-payment-modal';

export const CustomerOrderList = () => {
  const { contextPath } = useContext(TenantContext);
  const { listOrders } = useCustomerOrderApiV1();
  const { searchSummaryData, getConfigurations } = useCustomerAPI();
  const { listLocations } = useCustomerLocationApiV1();
  const [orders, setOrders] = useState<Pagination<CustomerOrder>>();
  const [locations, setLocations] = useState<Location[]>();
  const [request, setRequest] = useState<PaginationRequest>({ pageSize: 10, pageNumber: 1 });
  const [expandedOrderId, setExpandedOrderId] = useState<number>();
  const [summaryData, setSummaryData] = useState<InvoiceSearchSummaryData>();
  const [paymentModal, setPaymentModal] = useState<ReactNode | undefined>(undefined);
  const [portalPaymentEnabled, setPortalPaymentEnabled] = useState<boolean>(false);
  const customer = useCustomer();

  useEffect(() => {
    loadRefetchables();
  }, [request, customer]);

  useEffect(() => {
    listLocations().then(setLocations);
    getConfigurations(['CONF_CUSTOMER_PORTAL_PAYMENT']).then((configs) =>
      setPortalPaymentEnabled(configs.find((_) => _.variable == 'CONF_CUSTOMER_PORTAL_PAYMENT')?.value == '1'),
    );
  }, []);

  async function loadRefetchables() {
    if (!customer) {
      return;
    }
    await searchSummaryData({
      sortBy: 'dueDate',
      sortDirection: 'desc',
      customerId: customer!.id,
    }).then((res) => setSummaryData(res));
    await listOrders(request.pageSize || 10, request.pageNumber || 1).then(setOrders);
  }

  useEffect(() => {});

  return (
    <div className={'flex flex-column gap-8'}>
      {locations ? (
        <div className="summary-row">
          <Card className="flex flex-column grow">
            <span>Past Due</span>
            <span>{displayMoneyFormat(summaryData?.pastDue, locations[0].currency as Currency)}</span>
          </Card>
          <Card className="flex flex-column grow">
            <span>Payable</span>
            <span>{displayMoneyFormat(summaryData?.payable, locations[0].currency)}</span>
          </Card>
          {(summaryData?.payable ?? 0) > 1 && portalPaymentEnabled && !customer!.paymentBan && (
            <div className={'justify-self-center max-h-[33%] !border-none'}>
              <EcommerceButton
                buttonStyle={'primary'}
                onClick={() =>
                  setPaymentModal(
                    <CustomerPaymentModal
                      isOpen={true}
                      close={async () => {
                        setPaymentModal(undefined);
                        await loadRefetchables();
                      }}
                      balance={convertToMoney(summaryData?.payable ?? 0, locations[0].currency as Currency).toUnit()}
                      currency={locations[0].currency}
                    />,
                  )
                }
              >
                Pay Statement
              </EcommerceButton>
            </div>
          )}
        </div>
      ) : (
        <Spinner />
      )}
      <div className="customer-order-list flex flex-column">
        <table className="order-table">
          <thead>
            <tr>
              <th></th>
              <th className="text-left">Order No</th>
              <th className="text-left">Order Date</th>
              <th className="text-left">Location</th>
              <th className="text-right">Total price</th>
              <th className="text-right">Balance</th>
              <th className="text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders?.items.map((o) => {
              const location = locations?.find((l) => l.id === o.locationId);
              return (
                <React.Fragment key={o.id}>
                  <tr
                    onClick={() => {
                      window.location.pathname = `${contextPath}account/view_orders/${o.id}`;
                    }}
                  >
                    <td className="toggle-col">
                      <button
                        className={classNames('toggle-button', { expanded: o.id === expandedOrderId })}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedOrderId((e) => (e === o.id ? undefined : o.id));
                        }}
                      >
                        <span
                          className={classNames('fa', {
                            'fa-chevron-up': o.id === expandedOrderId,
                            'fa-chevron-down': o.id !== expandedOrderId,
                          })}
                        ></span>
                      </button>
                    </td>
                    <td className="order-number">
                      <a href={`${contextPath}account/view_orders/${o.id}`}>{o.orderNumber}</a>
                    </td>
                    <td className="requested-date">{o.dateAdded.toFormat('D t')}</td>
                    <td className="location">{location?.name}</td>
                    <td className="price">
                      {displayMoneyFormat(_.sum(o.invoices.map((inv) => inv.total)), location?.currency)}
                    </td>
                    <td>
                      {displayMoneyFormat(sum(o.invoices.map((_) => _.balance)), locations?.find((_) => _)!.currency)}
                    </td>
                    <td className="status">{toCustomerOrderStatus(o)}</td>
                  </tr>
                  {o.id === expandedOrderId && (
                    <tr className="child-orders">
                      <td colSpan={100}>
                        <table>
                          <thead>
                            <tr>
                              <th className="text-left">Equipment</th>
                              <th className="text-right">Qty</th>
                              <th className="text-left">Duration</th>
                              <th className="text-right">Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {o.products.map((op) => (
                              <tr key={op.id}>
                                <td className="text-left flex gap-2 items-center">
                                  <img
                                    className="product-img"
                                    src={`${contextPath}image/product_image/${op.product.id}/THUMB`}
                                  ></img>
                                  <span>{op.product.name}</span>
                                </td>
                                <td className="text-right">
                                  {op.quantity === Math.floor(op.quantity) ? op.quantity : op.quantity.toFixed(2)}
                                </td>
                                <td className="text-left">
                                  {op.rentalEndDate && (
                                    <div>
                                      Duration:{' '}
                                      {Interval.fromDateTimes(op.rentalStartDate, op.rentalEndDate)
                                        .toDuration()
                                        .rescale()
                                        .toHuman()}
                                    </div>
                                  )}
                                  <div>From: {op.rentalStartDate.toFormat('D t')}</div>
                                  <div>To: {op.rentalEndDate?.toFormat('D t') || '-'}</div>
                                </td>
                                <td className="text-right">
                                  {displayMoneyFormat(
                                    _.sum(
                                      o.invoices
                                        .flatMap((inv) => inv.lineItems)
                                        .filter((li) => li.type === 'OrderProduct' && li.typeId === op.id.toString())
                                        .map((li) => li.total),
                                    ),
                                    location?.currency,
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        <Paginator
          className="w-full"
          pagination={orders?.pagination}
          request={request}
          setRequest={(req) =>
            setRequest((ex) => {
              return { ...ex, ...req };
            })
          }
        />
      </div>
      {paymentModal}
    </div>
  );
};

function toCustomerOrderStatus(order: CustomerOrder) {
  switch (order.status) {
    case OrderStatus.Requested:
      return <div className="status-label warn">Pending</div>;
    case OrderStatus.Approved:
      switch (order.returnStatus) {
        case OrderReturnStatus.NotStarted:
          return <div className="status-label success">Approved</div>;
        case OrderReturnStatus.OnRent:
          return <div className="status-label success">Rental Started</div>;
        case OrderReturnStatus.OffRent:
        case OrderReturnStatus.Complete:
          return <div className="status-label success">Rental Returned</div>;
      }
      break;
    case OrderStatus.Quote:
      return <div className="status-label">Quote</div>;
    case OrderStatus.Cancelled:
      return <div className="status-label">Cancelled</div>;
    case OrderStatus.Missed:
      return <div className="status-label">Cancelled</div>;
  }
}
